import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import image from "../asset/image.jpeg";
import { Helmet } from 'react-helmet';
import { signUpWithEmail, signInWithGoogle } from "../services/firebaseAuthService"; // Added Google import
import { useAuth } from "../context/AuthContext"; // Import useAuth to check authentication status
import { motion, AnimatePresence } from "framer-motion";
import { BlippLogo } from "../components/BlippLogo";

function Register() {
  const { currentUser, isAnonymous } = useAuth();
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [reenterPassword, setReenterPassword] = useState("");
  const [popupMessage, setPopupMessage] = useState("");
  const [popupType, setPopupType] = useState("");
  const [step, setStep] = useState(1); // 1: email, 2: password
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [displayNameError, setDisplayNameError] = useState("");

  useEffect(() => {
    if (currentUser && !isAnonymous) {
      navigate("/chat");
    }
  }, [currentUser, isAnonymous, navigate]);

  useEffect(() => {
  if (popupMessage) {
    const timer = setTimeout(() => {
      setPopupMessage("");
    }, 2000); // 2000ms = 4 seconds

    return () => clearTimeout(timer); // Clear timer on unmount or when popupMessage changes
  }
}, [popupMessage]);

  const handleEmailContinue = (e) => {
    e.preventDefault();
    // if (!displayName.trim()) {
    //   setDisplayNameError("Username is required.");
    //   return;
    // }
    if (!email) {
      setEmailError("Email is required.");
      return;
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      setEmailError("Please enter a valid email address.");
      return;
    }
    setDisplayNameError("");
    setEmailError("");
    setPopupMessage("");
    setStep(2);
  };

  const handlePasswordContinue = async (e) => {
    e.preventDefault();
    // Validate password length
    if (password.length < 6) {
      setPasswordError("Password must be at least 6 characters long.");
      return;
    }
    if (password !== reenterPassword) {
      setPasswordError("Passwords do not match.");
      return;
    }
    setPasswordError("");
    try {
      await signUpWithEmail(email, password, displayName);
      setPopupMessage("Registration successful!");
      setPopupType("success");
      navigate("/chat");
    } catch (error) {
      if (error.code === "auth/email-already-in-use" || (error.message && error.message.includes("EMAIL_EXISTS"))) {
        setPasswordError("This email is already registered. Please use a different email or sign in with your existing account.");
        setPopupMessage("This email is already registered. Please use a different email or sign in with your existing account.");
        setPopupType("error");
      } else {
        setPasswordError("An error occurred during registration");
        setPopupMessage("An error occurred during registration");
        setPopupType("error");
      }
    }
  };

  const handleGoogleSignUp = async () => {
    try {
      await signInWithGoogle();
      setPopupMessage("Sign in successful!");
      setPopupType("success");
      navigate("/chat"); // Redirect after login
    } catch (error) {
      setPopupMessage("Failed to sign in with Google");
      setPopupType("error");
    }
  };

  return (
    <>
      <Helmet>
        <meta charSet="UTF-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="description" content="Register for an account on BlippAI" />
        <meta name="keywords" content="Register, AI, Account, BlippAI, Signup" />
        <meta name="author" content="BlippAI Team" />
        <meta property="og:title" content="BlippAI - Register" />
        <meta property="og:description" content="Create an account on BlippAI" />
        <meta property="og:image" content={image} />
        <meta property="og:url" content="https://blippai.com/register" />
        <title>BlippAI - Register</title>
      </Helmet>

      <div className="bg-background min-h-screen">
        <div className="container mx-auto px-6 py-12 max-w-md">
          {/* AnimatePresence wrapper around the popup */}
          <AnimatePresence>
            {popupMessage && (
              <motion.div
                key="popup"
                initial={{ opacity: 0, x: 100 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 100 }}
                transition={{ type: "spring", stiffness: 200, damping: 20 }}
                className={`fixed top-6 right-8 z-50 w-80 p-4 rounded-lg text-white shadow-lg
                  ${
                    popupType === "success"
                      ? "bg-green-600"
                      : popupType === "info"
                      ? "bg-blue-600"
                      : "bg-red-600"
                  }`}
              >
                {popupMessage}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Top center BlippAI branding */}
          <div className="text-center mb-8 flex items-center justify-center">
            <a href="/" aria-label="BlippAI home"><BlippLogo className="h-9 w-auto" /></a>
          </div>

          {/* Main heading */}
          <h2 className="text-3xl font-semibold text-foreground text-center mb-8">
            {step === 1 ? "Create an account" : "Set your password"}
          </h2>

          {step === 1 ? (
            <form onSubmit={handleEmailContinue} className="mb-6">
              {/* <div className="mb-6">
                <label className="block text-sm font-medium text-primary mb-1" htmlFor="username">
                  Username
                </label>
                <input
                  className="w-full px-4 py-3 bg-transparent border border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  id="username"
                  name="username"
                  type="text"
                  value={displayName}
                  onChange={e => setDisplayName(e.target.value)}
                  required
                  autoFocus
                />
                {displayNameError && (
                  <p className="text-red-500 text-sm mt-1">{displayNameError}</p>
                )}
              </div> */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-primary mb-1" htmlFor="email">
                  Email address
                </label>
                <input
                  className="w-full px-4 py-3 bg-transparent border border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  id="email"
                  name="email"
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                />
                {emailError && (
                  <p className="text-red-500 text-sm mt-1">{emailError}</p>
                )}
              </div>
              <button
                type="submit"
                className="w-full bg-primary text-primary-foreground py-3 rounded-md font-medium hover:bg-blue-400 transition-colors duration-300"
              >
                Continue
              </button>
            </form>
          ) : (
            <form onSubmit={handlePasswordContinue} className="mb-6">
              <div className="mb-4 flex items-center justify-between border border-gray-700 rounded-md px-4 py-3 bg-gray-900">
                <span className="text-gray-400 text-sm">{email}</span>
                <button 
                  type="button" 
                  onClick={() => setStep(1)}
                  className="text-primary hover:underline text-sm font-medium"
                >
                  Edit
                </button>
              </div>
              <div className="mb-6">
                <label className="block text-sm font-medium text-primary mb-1" htmlFor="password">
                  Password
                </label>
                <div className="relative">
                  <input
                    className="w-full px-4 py-3 bg-transparent border border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent pr-12"
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required
                  />
                  <button 
                    type="button" 
                    onClick={() => setShowPassword(v => !v)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-300 hover:text-gray-100"
                    tabIndex={-1}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? (
                      <svg width="20" height="20" fill="none" viewBox="0 0 24 24">
                        <path d="M12 5C7 5 2.73 8.11 1 12c1.73 3.89 6 7 11 7s9.27-3.11 11-7c-1.73-3.89-6-7-11-7zm0 12c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5z" fill="currentColor"/>
                        <circle cx="12" cy="12" r="2.5" fill="currentColor"/>
                      </svg>
                    ) : (
                      <svg width="20" height="20" fill="none" viewBox="0 0 24 24">
                        <path d="M1 12C2.73 8.11 7 5 12 5c2.73 0 5.19.81 7.07 2.13M23 12c-1.73 3.89-6 7-11 7-2.73 0-5.19-.81-7.07-2.13M3.27 3.27l17.46 17.46" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M9.53 9.53A3.5 3.5 0 0 0 12 16.5c.96 0 1.84-.38 2.47-1.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </button>
                </div>
              </div>
              <div className="mb-6">
                <label className="block text-sm font-medium text-primary mb-1" htmlFor="reenterPassword">
                  Re-enter Password
                </label>
                <input
                  className="w-full px-4 py-3 bg-transparent border border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  id="reenterPassword"
                  name="reenterPassword"
                  type="password"
                  value={reenterPassword}
                  onChange={e => setReenterPassword(e.target.value)}
                  required
                />
                {passwordError && (
                  <p className="text-red-500 text-sm mt-1">{passwordError}</p>
                )}
              </div>
              <button
                type="submit"
                className="w-full bg-primary text-primary-foreground py-3 rounded-md font-medium hover:bg-blue-400 transition-colors duration-300"
              >
                Sign Up
              </button>
            </form>
          )}

          <p className="text-center text-sm text-gray-300 my-6">
            Already have an account?{' '}
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="text-primary hover:underline bg-transparent border-none cursor-pointer"
            >
              Sign in
            </button>
          </p>

          <div className="flex items-center my-8">
            <div className="flex-grow border-t border-gray-700"></div>
            <span className="px-4 text-sm text-gray-400">OR</span>
            <div className="flex-grow border-t border-gray-700"></div>
          </div>

          <div className="space-y-4">
            <button
              type="button"
              onClick={handleGoogleSignUp}
              className="w-full flex items-center justify-center px-4 py-3 border border-gray-700 rounded-md text-gray-200 hover:bg-gray-800 transition-colors duration-300"
            >
              <img 
                alt="Google logo" 
                className="w-5 h-5 mr-2" 
                src="data:image/svg+xml,%3Csvg width='48' height='48' viewBox='0 0 48 48' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cg clipPath='url(%23clip0_17_40)'%3E%3Cpath d='M47.617 24.552c0-1.636-.146-3.2-.418-4.704H24.48v9.02h13.02c-.528 2.84-2.12 5.24-4.52 6.86v5.68h7.32c4.28-3.94 6.317-9.74 6.317-16.856z' fill='%234285F4'/%3E%3Cpath d='M24.48 48c6.12 0 11.24-2.04 14.987-5.56l-7.32-5.68c-2.04 1.36-4.66 2.16-7.667 2.16-5.9 0-10.9-3.98-12.687-9.32H4.97v5.84C8.7 43.98 16.02 48 24.48 48z' fill='%2334A853'/%3E%3Cpath d='M11.793 29.6A13.98 13.98 0 0 1 10.48 24c0-1.96.36-3.86 1.313-5.6v-5.84H4.97A23.98 23.98 0 0 0 .48 24c0 3.98.96 7.76 2.64 11.04l8.673-5.44z' fill='%23FBBC05'/%3E%3Cpath d='M24.48 9.52c3.34 0 6.32 1.14 8.68 3.38l6.48-6.48C35.72 2.36 30.6 0 24.48 0 16.02 0 8.7 4.02 4.97 10.16l8.673 5.84c1.787-5.34 6.787-9.32 12.687-9.32z' fill='%23EA4335'/%3E%3C/g%3E%3Cdefs%3E%3CclipPath id='clip0_17_40'%3E%3Cpath fill='%23fff' d='M0 0h48v48H0z'/%3E%3C/clipPath%3E%3C/defs%3E%3C/svg%3E"
              />
              Continue with Google
            </button>
          </div>

          {/* <div className="mt-12 text-center text-xs text-gray-400">
            <button className="text-primary hover:underline bg-transparent border-none cursor-pointer">Terms of Use</button>
            <span className="mx-2">|</span>
            <button className="text-primary hover:underline bg-transparent border-none cursor-pointer">Privacy Policy</button>
          </div> */}
        </div>
      </div>
    </>
  );
}

export default Register;
